import { getIdentity } from "@/shared/api/identity";
import { HANDLER_BUDGET_MS } from "@/features/intelligence/lib/frontend-tool-outcome";

type IntelligenceAccess = "checking" | "ok" | "unknown";

export interface AccessAnswer {
  readonly access: Exclude<IntelligenceAccess, "checking">;
}

export const NOTHING_ARMED: AccessAnswer = { access: "unknown" };

const ARMING_WAIT_MS = HANDLER_BUDGET_MS / 2;

export interface AccessProbeSource {
  now: () => AccessAnswer | null;
  inFlight: () => Promise<void> | null;
  warm: () => void;
}

let settled: AccessAnswer | null = null;
let flight: Promise<void> | null = null;

function accessAnswerNow(): AccessAnswer | null {
  return settled;
}

function accessProbeInFlight(): Promise<void> | null {
  return flight;
}

export function warmAccessProbe(): void {
  void ensureAccessProbe();
}

const LIVE_ACCESS_PROBE: AccessProbeSource = {
  now: accessAnswerNow,
  inFlight: accessProbeInFlight,
  warm: warmAccessProbe,
};

export async function ensureAccessProbe(): Promise<AccessAnswer> {
  if (settled) return settled;
  const running = flight;
  if (running) {
    await running;
    return settled ?? NOTHING_ARMED;
  }

  const run = (async (): Promise<void> => {
    try {
      const identity = await getIdentity();
      if (!identity.financialAdvisorId) return;
      settled = { access: "ok" };
    } catch {}
  })();

  flight = run;
  void run.finally(() => {
    if (flight === run) flight = null;
  });

  await run;
  return settled ?? NOTHING_ARMED;
}

export async function accessWhenKnown(
  source: AccessProbeSource = LIVE_ACCESS_PROBE,
  budgetMs: number = ARMING_WAIT_MS,
  setTimer: (fn: () => void, ms: number) => unknown = (fn, ms) => setTimeout(fn, ms),
  clearTimer: (handle: unknown) => void = (h) => clearTimeout(h as never),
): Promise<AccessAnswer> {
  const held = source.now();
  if (held !== null) return held;

  const running = source.inFlight();
  if (running === null) {
    source.warm();
    return NOTHING_ARMED;
  }

  let handle: unknown = null;
  const expiry = new Promise<void>((resolve) => {
    handle = setTimer(() => resolve(), budgetMs);
  });
  try {
    await Promise.race([running.catch(() => undefined), expiry]);
  } finally {
    if (handle !== null) clearTimer(handle);
  }
  return source.now() ?? NOTHING_ARMED;
}

export function resetAccessProbe(): void {
  settled = null;
  flight = null;
}
